<template>
  <div class="calendar" ref="calendar">
    <div class="groupInpLine">

      <div class="placeholder-container bigInp_centered"
        :class="{ 'error': startInvalid }">
        <input type="text"
          placeholder=" "
          name="start"
          class="mainInp bigMainInp"
          :class="{ 'focus': startFocused, 'nofocus': endFocused, 'calendarInp': !endFocused }"
          :value="startFormated"
          @focus="onInputFocus(0)"
          ref="startInput"
          readonly />
        <label for="start">{{ startPlaceholder }}</label>
        <transition name="clearInput">
          <button
            v-if="startOrHover && show"
            type="button"
            class="clearInput"
            @click.prevent="clearInput(0)">
          </button>
        </transition>
        <div class="error-input-message" v-if="startInvalid && !show ">
          {{ $t('qic_error_empty_input') }}
        </div>
      </div>

      <div class="placeholder-container bigInp_centered"
        :class="{ 'error': endInvalid }">
        <input type="text"
          placeholder=" "
          name="end"
          class="mainInp bigMainInp"
          :class="{  'focus': endFocused,  'nofocus': startFocused, 'calendarInp': !startFocused }"
          :value="endFormated"
          @focus="onInputFocus(1)"
          ref="endInput"
          readonly />
        <label for="end">{{ endPlaceholder }}</label>
        <button
          v-if="endOrHover && show"
          type="button"
          class="clearInput"
          @click.prevent="clearInput(1)">
        </button>
        <div class="error-input-message" v-if="endInvalid  && !show ">
          {{ $t('qic_error_empty_input') }}
        </div>
      </div>

    </div>
    <div class="calendar-container"
      v-if="show || mobilePopupOn"
      @mouseleave="onContainerMouseLeave()">
      <calendar
        @daymouseenter="onDayMouseEnter"
        @dayclick="onDayClick"
        trim-weeks
        :first-day-of-week="1"
        :update-on-input="true"
        :step="1"
        :locale="locale"
        :min-date="minDate"
        :max-date="maxDate"
        :masks="masks"
        :columns="layout.columns"
        :rows="layout.rows"
        :attributes="calendarAttributes"
        :is-expanded="layout.isExpanded">
        <template v-slot:header="page">
          <div class="vc-title">{{ page.monthLabel }} {{ page.yearLabel }}</div>
        </template>
        <template v-slot:header-left-button>
          <div class="prevMonth"></div>
        </template>
        <template v-slot:header-right-button>
          <div class="nextMonth"></div>
        </template>
      </calendar>
    </div>
  </div>
</template>

<script>
import { isMobileOnly } from 'mobile-device-detect'
import format from 'date-fns/format'
import isAfter from 'date-fns/isAfter'
import isBefore from 'date-fns/isBefore'
import { enGB as en, arSA as ar } from 'date-fns/locale'
import Calendar from 'v-calendar/lib/components/calendar.umd'
export default {
  name: 'CalendarRange',
  components: {
    Calendar
  },
  props: {
    value: {},
    minDate: {},
    maxDate: {},
    invalid: {},
    startPlaceholder: { type: String, default: '' },
    endPlaceholder: { type: String, default: '' },
    mobilePopupOn: { type: Boolean, default: false }
  },
  data () {
    return {
      masks: {
        input: 'D MMM YYYY',
        weekdays: 'WWW'
      },
      focusedDate: 0,
      calendarShowed: false,
      hoverDay: null,
      range: {
        start: null,
        end: null
      }
    }
  },
  computed: {
    locale () {
      return this.$i18n.locale
    },
    formatLocale () {
      if (this.locale === 'ar') {
        return ar
      } else {
        return en
      }
    },
    isMobileOnly () {
      return isMobileOnly
    },
    layout () {
      if (this.isMobileOnly) {
        let mounthCount = 12
        if (this.minDate && this.maxDate) {
          mounthCount = (this.maxDate.getFullYear() - this.minDate.getFullYear()) * 12
          mounthCount -= this.minDate.getMonth()
          mounthCount += this.maxDate.getMonth() + 1
          if (mounthCount < 0) mounthCount = 0
          if (mounthCount > 12) mounthCount = 12
        }
        return {
          columns: 1,
          rows: mounthCount,
          isExpanded: true,
          mobile: true
        }
      } else {
        return {
          columns: 2,
          rows: 1,
          isExpanded: false,
          mobile: false
        }
      }
    },
    start () {
      return this.range.start
    },
    end () {
      return this.range.end
    },
    startOrHover () {
      if (!this.hoverDay) return this.start
      return this.getUpdatedRange(this.hoverDay).start
    },
    endOrHover () {
      if (!this.hoverDay) return this.end
      return this.getUpdatedRange(this.hoverDay).end
    },
    startFocused () {
      let focused = this.focusedDate
      if (this.hoverDay) focused = this.getUpdatedRange(this.hoverDay).updated
      return focused === 0 && this.show
    },
    endFocused () {
      let focused = this.focusedDate
      if (this.hoverDay) focused = this.getUpdatedRange(this.hoverDay).updated
      return focused === 1 && this.show
    },
    calendarFocused () {
      return this.endFocused || this.startFocused
    },
    startFormated () {
      return this.formatDate(this.startOrHover)
    },
    endFormated () {
      return this.formatDate(this.endOrHover)
    },
    show () {
      return this.calendarShowed
    },
    startInvalid () {
      return !!this.invalid?.start || false
    },
    endInvalid () {
      return !!this.invalid?.end || false
    },
    startValid () {
      return !this.startInvalid && this.start
    },
    endValid () {
      return !this.endInvalid && this.end
    },
    formatString () {
      return this.locale === 'ar' ? 'd MMMM yyyy' : 'd MMM yyyy'
    },
    rangeAttributes () {
      let range = null
      if (this.range.start && this.range.end) {
        range = this.range
      } else if (!this.range.start && this.range.end) {
        range = this.range.end
      } else if (this.range.start && !this.range.end) {
        range = this.range.start
      }
      return {
        key: 'range',
        highlight: {
          start: {
            class: 'highlight-start-bg-class',
            contentClass: 'highlight-start-class'
          },
          base: {
            class: 'highlight-bg-class',
            contentClass: 'highlight-class'
          },
          end: {
            class: 'highlight-end-bg-class',
            contentClass: 'highlight-end-class'
          }
        },
        dates: range
      }
    },
    rangeOrHoverAttributes () {
      const range = { start: this.startOrHover, end: this.endOrHover }
      let startBgClass = 'highlight-start-bg-class'
      let startClass = 'highlight-start-class'
      let endBgClass = 'highlight-end-bg-class'
      let endClass = 'highlight-end-class'
      if (this.range.start && this.range.end) {
        if (isBefore(range.start, this.range.start)) {
          range.end = this.range.start
          startClass = 'hover-start-class'
          startBgClass = 'hover-start-bg-class'
        } else if (isAfter(range.end, this.range.end)) {
          range.start = this.range.end
          endClass = 'hover-end-class'
          endBgClass = 'hover-end-bg-class'
        } else {
          return null
        }
      }
      return {
        key: 'range-hover',
        highlight: {
          start: {
            class: startBgClass,
            contentClass: startClass
          },
          base: {
            class: 'highlight-bg-class',
            contentClass: 'highlight-class'
          },
          end: {
            class: endBgClass,
            contentClass: endClass
          }
        },
        dates: range
      }
    },
    calendarAttributes () {
      const rangeAttributes = this.rangeAttributes
      const rangeOrHoverAttributes = this.rangeOrHoverAttributes
      const attributes = []
      attributes.push(rangeAttributes)
      if (this.startOrHover && this.endOrHover && rangeOrHoverAttributes) {
        attributes.push(rangeOrHoverAttributes)
      }
      return attributes
    }
  },
  methods: {
    formatDate (value) {
      const marker = this.locale === 'ar' ? '\u200f' : ''
      return value ? format(value, `${marker}${this.formatString}`, { locale: this.formatLocale }) : ''
    },
    dayFocused (date) {
      this.range = this.getUpdatedRange(date)
      this.focusDate(this.focusedDate === 0 ? 1 : 0)
      this.emitInput()
    },
    focusDate (inp) {
      this.focusedDate = inp
    },
    emitInput () {
      const result = { start: this.start, end: this.end }
      this.$emit('input', result)
      if (result.start && result.end && !this.mobilePopupOn) {
        this.hideCalendar()
      }
    },
    showCalendar () {
      this.updateRange()
      this.calendarShowed = true
    },
    hideCalendar () {
      this.calendarShowed = false
      this.hoverDay = null
    },
    updateRange () {
      this.range = { ...this.value }
    },
    getUpdatedRange (date) {
      const result = { ...this.range, updated: this.focusedDate }
      if (!result.start || !result.end) {
        if (this.focusedDate === 0 && result.end && isAfter(date, result.end)) {
          result.start = result.end
          result.end = date
          result.updated = 1
        } else if (this.focusedDate === 1 && result.start && isBefore(date, result.start)) {
          result.end = result.start
          result.start = date
          result.updated = 0
        } else if (this.focusedDate === 0) {
          result.start = date
        } else {
          result.end = date
        }
      } else if ((isAfter(date, result.start) && isBefore(date, result.end))) {
        if (this.focusedDate === 0) result.start = date
        else result.end = date
      } else {
        if (isBefore(date, result.end)) {
          result.start = date
          result.updated = 0
        } else {
          result.end = date
          result.updated = 1
        }
      }
      return result
    },
    onInputFocus (num) {
      this.focusedDate = num
      this.showCalendar()
    },
    clearInput (input) {
      if (input === 0) {
        this.range.start = null
        this.range.end = null
      }
      if (input === 1) {
        this.range.end = null
      }
      this.emitInput()
    },
    onContainerMouseLeave () {
      this.hoverDay = null
    },
    onDayMouseEnter ({ date, isDisabled }) {
      this.hoverDay = isDisabled ? ((this.minDate && date < this.minDate) ? this.minDate : (this.maxDate || date)) : date
    },
    onDayClick ({ date, isDisabled }) {
      if (!isDisabled) {
        this.dayFocused(date)
      }
    },
    closeCalendar ($event) {
      const el = $event.composedPath()
      const calendareBody = this.$refs.calendar
      if (!el.includes(calendareBody)) {
        this.hideCalendar()
      }
    }
  },
  watch: {
    calendarFocused (value) {
      this.$emit('focusChanged', value)
    }
  },
  mounted () {
    this.updateRange()
    if (!this.mobilePopupOn) {
      document.addEventListener('click', this.closeCalendar)
    }
  },
  beforeDestroy () {
    if (!this.mobilePopupOn) {
      document.removeEventListener('click', this.closeCalendar)
    }
  }
}
</script>
<style scoped>
  .acceptInp.mainInp:hover{
    background-image: url(~../../assets/images/check.svg);
    background-repeat: no-repeat;
  }
  .clearInput-enter-active, .clearInput-leave-active {
    transition: opacity 0.1s;
  }
  .clearInput-enter, .clearInput-leave-to {
    opacity: 0;
  }
  .calendarInp {
    transition: 0s;
  }
</style>
<style lang="scss" scoped>
  .error-input-message {
    color: #ef3030;
    font-size: 12px;
    line-height: 1;
    margin-top: 8px;
  }
</style>
